import React, { useEffect, useState } from 'react'
import { InputNumber, Tabs } from 'antd'
import CustomModal from 'components/global/CustomModal'
import CustomModalFooter from 'components/global/CustomModalFooter'
import { leagueGamesStore } from 'state/league/leageGames'
import { leagueEventStore } from 'state/league/leagueEvent'
import { myStore } from 'state/store'
import { failureMessage } from 'api/global'
import CustomAvatar from 'components/global/CustomAvatar'
import {
  useCreateLeagueFoul,
  useCreateLeagueGoal,
  useCreateLeagueTimeout,
} from 'api/league/leagueLog/leagueLog'
import { useGetTeamMembers } from 'api/Team/Team'
import { TeamKind } from 'types/league/leagueLog'
import { TeamMemberRecord } from 'types/team/team'

const { TabPane } = Tabs

// ── shared sub-components ────────────────────────────────────────────────────

const TeamCard = ({
  name,
  photo,
  label,
  selected,
  onClick,
}: {
  name: string
  photo?: string
  label: string
  selected: boolean
  onClick: () => void
}) => (
  <div
    className="flex flex-column align-center pointer radius-12"
    onClick={onClick}
  >
    <div
      className="flex flex-column align-center justify-center px-15px py-12px radius-12 w-full"
      style={{
        border: selected ? '2px solid #E69741' : '1px solid #EBEDF1',
        background: selected ? '#FFF8F0' : '#fff',
        minHeight: 95,
        gap: 12,
      }}
    >
      <CustomAvatar size={40} src={photo} name={name} />
      <span style={{ fontSize: 12, fontWeight: 600, color: '#000229' }}>{name}</span>
    </div>
    <span
      style={{
        fontSize: 12,
        fontWeight: 600,
        color: '#908F98',
        textAlign: 'center',
        padding: '6px 0',
      }}
    >
      {label}
    </span>
  </div>
)

const abbrevName = (first = '', last = '') => {
  const f = first.trim()
  const l = last.trim()
  if (!f && !l) return '—'
  return f ? `${f[0]}. ${l}` : l
}

const JerseyCard = ({
  member,
  jerseyNum,
  selected,
  onClick,
}: {
  member: TeamMemberRecord
  jerseyNum: number
  selected: boolean
  onClick: () => void
}) => {
  const name = abbrevName(member.member.firstName, member.member.lastName)
  return (
    <div
      className="flex flex-column align-center justify-center pointer radius-12"
      style={{
        border: selected ? '2px solid #E69741' : '1px solid #EBEDF1',
        background: selected ? '#FFF8F0' : '#fff',
        padding: '12px 8px',
        gap: 8,
        flex: '1 0 0',
        minWidth: 0,
      }}
      onClick={onClick}
    >
      <div
        style={{
          width: 52,
          height: 52,
          borderRadius: 8,
          background: selected ? '#E69741' : '#E8E8EA',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: 20,
          fontWeight: 700,
          color: selected ? '#fff' : '#000',
          flexShrink: 0,
        }}
      >
        {jerseyNum}
      </div>
      <span
        style={{
          fontSize: 12,
          fontWeight: 500,
          color: '#000229',
          textAlign: 'center',
          maxWidth: '100%',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {name}
      </span>
    </div>
  )
}

const PlayerGrid = ({
  members,
  selected,
  multi,
  onToggle,
}: {
  members: TeamMemberRecord[]
  selected: string[]
  multi: boolean
  onToggle: (id: string) => void
}) => {
  if (!members.length)
    return <p className="text-gray text-14 mt-10px">No players in this team</p>

  const rows: TeamMemberRecord[][] = []
  for (let i = 0; i < members.length; i += 3) rows.push(members.slice(i, i + 3))

  return (
    <div className="flex flex-column gap-10px mt-10px">
      {rows.map((row, ri) => (
        <div key={ri} className="flex gap-10px">
          {row.map((m, ci) => (
            <JerseyCard
              key={m._id}
              member={m}
              jerseyNum={ri * 3 + ci + 1}
              selected={selected.includes(m.memberId)}
              onClick={() => {
                if (multi) {
                  onToggle(m.memberId)
                } else {
                  onToggle(m.memberId)
                }
              }}
            />
          ))}
          {row.length < 3 &&
            Array.from({ length: 3 - row.length }).map((_, i) => (
              <div key={`pad-${i}`} style={{ flex: '1 0 0', minWidth: 0 }} />
            ))}
        </div>
      ))}
    </div>
  )
}

// ── main component ────────────────────────────────────────────────────────────

const EnterLog: React.FC = () => {
  const [activeTab, setActiveTab] = useState('goal')

  // per-tab team selection
  const [goalTeam, setGoalTeam] = useState<TeamKind>('home')
  const [foulTeam, setFoulTeam] = useState<TeamKind>('home')
  const [timeoutTeam, setTimeoutTeam] = useState<TeamKind>('home')

  // foul player selections
  const [foulPlayerIds, setFoulPlayerIds] = useState<string[]>([])
  const [againstPlayerIds, setAgainstPlayerIds] = useState<string[]>([])

  // goal player selection (single)
  const [goalPlayerId, setGoalPlayerId] = useState<string | null>(null)

  // goal score / timeout duration (controlled)
  const [goalScore, setGoalScore] = useState<number | null>(null)
  const [duration, setDuration] = useState<number | null>(null)

  const { enterLogModal, setEnterLogModal, selectedGame } = leagueGamesStore(store => ({
    enterLogModal: store.enterLogModal,
    setEnterLogModal: store.setEnterLogModal,
    selectedGame: store.selectedGame,
  }))
  const { leagueData } = leagueEventStore(store => ({ leagueData: store.leagueData }))
  const setModalBtnLoader = myStore(state => state.setModalBtnLoader)

  const { mutate: createFoul } = useCreateLeagueFoul()
  const { mutate: createTimeout } = useCreateLeagueTimeout()
  const { mutate: createGoal } = useCreateLeagueGoal()

  const { data: homeMembers, refetch: refetchHome } = useGetTeamMembers(
    selectedGame?.homeTeam?.team?._id || '',
    {},
  )
  const { data: awayMembers, refetch: refetchAway } = useGetTeamMembers(
    selectedGame?.awayTeam?.team?._id || '',
    {},
  )

  useEffect(() => {
    if (selectedGame?.homeTeam?.team?._id) refetchHome()
    if (selectedGame?.awayTeam?.team?._id) refetchAway()
  }, [selectedGame])

  const homePlayers = homeMembers?.records || []
  const awayPlayers = awayMembers?.records || []

  const goalPlayers = goalTeam === 'home' ? homePlayers : awayPlayers
  const foulPlayers = foulTeam === 'home' ? homePlayers : awayPlayers
  const againstPlayers = foulTeam === 'home' ? awayPlayers : homePlayers

  const closeModal = () => {
    setEnterLogModal(false)
    setActiveTab('goal')
    setGoalTeam('home')
    setFoulTeam('home')
    setTimeoutTeam('home')
    setFoulPlayerIds([])
    setAgainstPlayerIds([])
    setGoalPlayerId(null)
    setGoalScore(null)
    setDuration(null)
  }

  const toggleFoulPlayer = (id: string) =>
    setFoulPlayerIds(prev =>
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id],
    )

  const toggleAgainstPlayer = (id: string) =>
    setAgainstPlayerIds(prev =>
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id],
    )

  const toggleGoalPlayer = (id: string) =>
    setGoalPlayerId(prev => (prev === id ? null : id))

  const handleSave = () => {
    const leagueId: string = (leagueData as any)?._id || ''
    const orgId: string = (leagueData as any)?.organizationId || ''

    if (activeTab === 'goal') {
      if (!goalScore) return failureMessage('Enter the score')
      const score = goalScore
      setModalBtnLoader(true)
      const selectedTeamId =
        goalTeam === 'home'
          ? selectedGame.homeTeam._id
          : selectedGame.awayTeam._id
      createGoal(
        {
          ...(goalPlayerId ? { playerId: goalPlayerId } : {}),
          teamId: selectedTeamId,
          teamKind: goalTeam,
          gameId: selectedGame._id,
          leagueId,
          organizationId: orgId,
          score,
        },
        {
          onSuccess: () => {
            setModalBtnLoader(false)
            closeModal()
          },
          onError: () => {
            setModalBtnLoader(false)
          },
        },
      )
    } else if (activeTab === 'foul') {
      setModalBtnLoader(true)
      const selectedTeamId =
        foulTeam === 'home'
          ? selectedGame.homeTeam._id
          : selectedGame.awayTeam._id
      createFoul(
        {
          teamId: selectedTeamId,
          foulPlayerIds,
          againstPlayerIds,
          leagueId,
          organizationId: orgId,
          teamKind: foulTeam,
        },
        {
          onSuccess: () => {
            setModalBtnLoader(false)
            closeModal()
          },
          onError: () => {
            setModalBtnLoader(false)
          },
        },
      )
    } else if (activeTab === 'timeout') {
      setModalBtnLoader(true)
      const selectedTeamId =
        timeoutTeam === 'home'
          ? selectedGame.homeTeam._id
          : selectedGame.awayTeam._id
      createTimeout(
        { teamId: selectedTeamId, teamKind: timeoutTeam, leagueId, duration: duration ?? undefined },
        {
          onSuccess: () => {
            setModalBtnLoader(false)
            closeModal()
          },
          onError: () => {
            setModalBtnLoader(false)
          },
        },
      )
    }
  }

  const handleTabChange = (key: string) => {
    setActiveTab(key)
    setFoulPlayerIds([])
    setAgainstPlayerIds([])
    setGoalPlayerId(null)
    setGoalScore(null)
    setDuration(null)
  }

  if (!selectedGame) return null

  const homeName = selectedGame.homeTeam.team.name
  const awayName = selectedGame.awayTeam.team.name
  const homePhoto = selectedGame.homeTeam.team.profilePhoto?.thumb
  const awayPhoto = selectedGame.awayTeam.team.profilePhoto?.thumb

  return (
    <CustomModal
      openModal={enterLogModal}
      isCloseModalCustom
      closeModal={closeModal}
      title="Enter log"
      footer={
        <CustomModalFooter
          multibutton={false}
          btnAlignment="justify-end"
          btnText="Save"
          closeModal={closeModal}
          onSubmit={handleSave}
        />
      }
    >
      {/* game context strip */}
      <div
        className="flex align-center justify-between mb-20px px-15px py-10px radius-8"
        style={{ background: '#F8F9FA', border: '1px solid #EBEDF1' }}
      >
        <div className="flex align-center gap-10px">
          <CustomAvatar size={28} src={homePhoto} name={homeName} />
          <span className="text-14 w-600">{homeName}</span>
        </div>
        <span className="text-12 text-gray">vs</span>
        <div className="flex align-center gap-10px">
          <span className="text-14 w-600">{awayName}</span>
          <CustomAvatar size={28} src={awayPhoto} name={awayName} />
        </div>
      </div>

      <Tabs activeKey={activeTab} onChange={handleTabChange}>
        {/* ── GOAL tab ───────────────────────────────────────────── */}
        <TabPane tab="Goal" key="goal">
          <p className="text-14 w-600 mb-10px">Select Team</p>
          <div className="flex gap-15px mb-20px">
            <div style={{ flex: 1 }}>
              <TeamCard
                name={homeName}
                photo={homePhoto}
                label="Home Team"
                selected={goalTeam === 'home'}
                onClick={() => {
                  setGoalTeam('home')
                  setGoalPlayerId(null)
                }}
              />
            </div>
            <div style={{ flex: 1 }}>
              <TeamCard
                name={awayName}
                photo={awayPhoto}
                label="Away Team"
                selected={goalTeam === 'away'}
                onClick={() => {
                  setGoalTeam('away')
                  setGoalPlayerId(null)
                }}
              />
            </div>
          </div>

          <p className="text-14 w-600 mb-5px">Select Player (optional)</p>
          <PlayerGrid
            members={goalPlayers}
            selected={goalPlayerId ? [goalPlayerId] : []}
            multi={false}
            onToggle={toggleGoalPlayer}
          />

          <div className="mt-20px">
            <p className="text-14 w-600 mb-5px">Score</p>
            <InputNumber
              min={1}
              placeholder="1"
              className="br-12 w-full"
              value={goalScore}
              onChange={v => setGoalScore(v)}
              onWheel={e => e.currentTarget.blur()}
            />
          </div>
        </TabPane>

        {/* ── FOUL tab ───────────────────────────────────────────── */}
        <TabPane tab="Foul" key="foul">
          <p className="text-14 w-600 mb-10px">Select Team</p>
          <div className="flex gap-15px mb-20px">
            <div style={{ flex: 1 }}>
              <TeamCard
                name={homeName}
                photo={homePhoto}
                label="Home Team"
                selected={foulTeam === 'home'}
                onClick={() => {
                  setFoulTeam('home')
                  setFoulPlayerIds([])
                  setAgainstPlayerIds([])
                }}
              />
            </div>
            <div style={{ flex: 1 }}>
              <TeamCard
                name={awayName}
                photo={awayPhoto}
                label="Away Team"
                selected={foulTeam === 'away'}
                onClick={() => {
                  setFoulTeam('away')
                  setFoulPlayerIds([])
                  setAgainstPlayerIds([])
                }}
              />
            </div>
          </div>

          <p className="text-14 w-600 mb-5px">Select Player (optional)</p>
          <p className="text-12 text-gray mb-5px">Who committed the foul</p>
          <PlayerGrid
            members={foulPlayers}
            selected={foulPlayerIds}
            multi={true}
            onToggle={toggleFoulPlayer}
          />

          {againstPlayers.length > 0 && (
            <div className="mt-20px">
              <p className="text-14 w-600 mb-5px">Fouled against (optional)</p>
              <p className="text-12 text-gray mb-5px">Players from the opposing team</p>
              <PlayerGrid
                members={againstPlayers}
                selected={againstPlayerIds}
                multi={true}
                onToggle={toggleAgainstPlayer}
              />
            </div>
          )}
        </TabPane>

        {/* ── TIMEOUT tab ────────────────────────────────────────── */}
        <TabPane tab="Timeout" key="timeout">
          <p className="text-14 w-600 mb-10px">Select Team</p>
          <div className="flex gap-15px mb-20px">
            <div style={{ flex: 1 }}>
              <TeamCard
                name={homeName}
                photo={homePhoto}
                label="Home Team"
                selected={timeoutTeam === 'home'}
                onClick={() => setTimeoutTeam('home')}
              />
            </div>
            <div style={{ flex: 1 }}>
              <TeamCard
                name={awayName}
                photo={awayPhoto}
                label="Away Team"
                selected={timeoutTeam === 'away'}
                onClick={() => setTimeoutTeam('away')}
              />
            </div>
          </div>

          <p className="text-14 w-600 mb-5px">Duration (seconds)</p>
          <InputNumber
            min={1}
            placeholder="30"
            className="br-12 w-full"
            value={duration}
            onChange={v => setDuration(v)}
            onWheel={e => e.currentTarget.blur()}
          />
        </TabPane>
      </Tabs>
    </CustomModal>
  )
}

export default EnterLog
